import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { LOADING_STATE } from "src/stores/krediflow/ImportInvoiceFilesStore";
import { UI } from "@wwimmo/ui";
import styles from "./ImportInvoicesList.module.css";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { ColorStyle } from "src/utils/Colors";
import { INPUTWORKFLOW_FILE_STATE, INPUTWORKFLOW_STATE, INVOICE_ORIGIN } from "src/stores/krediflow/InvoiceEnums";
import { NetworkConfig } from "src/network/NetworkConfig";
import { IInputWorkflow, IInputWorkflowFile } from "src/stores/krediflow/InvoiceTypes";
import { Route, selectRoute } from "src/config/routes";

enum COLUMN {
    DESCRIPTION = 0,
    STATE = 1,
    ACTIONS = 2,
    TOGGLE = 3
}

enum COLUMN_WIDTH_DESKTOP {
    DESCRIPTION = 0.4,
    STATE = 0.35,
    ACTIONS = 0.2,
    TOGGLE = 0.05
}

enum COLUMN_WIDTH_MOBILE {
    DESCRIPTION = 0.4,
    STATE = 0.35,
    ACTIONS = 0.2,
    TOGGLE = 0.05
}

const ImportInvoicesListBase = () => {
    const { t } = useTranslation();
    const { importInvoiceFilesStore, uiStore, authStore } = useContext(RootStoreContext);

    const toggleDisplayedInputWorkflowFileDropdown = useCallback(
        (inputWorkflowId: string) => () => {
            importInvoiceFilesStore.toggleInputWorkflowFileDropdowns(inputWorkflowId);
        },
        [importInvoiceFilesStore]
    );

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [
            COLUMN_WIDTH_DESKTOP.DESCRIPTION,
            COLUMN_WIDTH_DESKTOP.STATE,
            COLUMN_WIDTH_DESKTOP.ACTIONS,
            COLUMN_WIDTH_DESKTOP.TOGGLE
        ];
        const columnWidthsMobile = [
            COLUMN_WIDTH_MOBILE.DESCRIPTION,
            COLUMN_WIDTH_MOBILE.STATE,
            COLUMN_WIDTH_MOBILE.ACTIONS,
            COLUMN_WIDTH_MOBILE.TOGGLE
        ];

        return uiStore.currentScreenSize > SCREEN_SIZE.TABLET ? columnWidthsDesktop : columnWidthsMobile;
    }, [uiStore.currentScreenSize]);

    const columnWidths = getColumnWidths();

    const loadingImportElement = (
        <div className="d-flex align-items-center">
            <div>
                <UI.RotatingSpinner noLogo size={20} />
            </div>
            <div>{t("screens.kredi_flow.document_import.list.loading_imports")}</div>
        </div>
    );

    const getInputWorkflowFileStateElement = (inputWorkflowFileState: INPUTWORKFLOW_FILE_STATE | undefined) => {
        const stateText = t(`screens.kredi_flow.document_import.input_workflow_file.state.${inputWorkflowFileState}`);

        let stateElement = undefined;

        if (inputWorkflowFileState) {
            switch (inputWorkflowFileState) {
                case INPUTWORKFLOW_FILE_STATE.NEW:
                    stateElement = stateText;
                    break;
                case INPUTWORKFLOW_FILE_STATE.MANUAL:
                    stateElement = <div className={`${styles.InputWorkflowFileTag} ${styles.Manual}`}>{stateText}</div>;
                    break;
                case INPUTWORKFLOW_FILE_STATE.READY:
                    stateElement = <div className={`${styles.InputWorkflowFileTag} ${styles.Ready}`}>{stateText}</div>;
                    break;
                case INPUTWORKFLOW_FILE_STATE.CLOSED:
                    stateElement = <div className={`${styles.InputWorkflowFileTag} ${styles.Closed}`}>{stateText}</div>;
                    break;
            }
        }

        return stateElement;
    };

    const onDeleteInputWorkflowFile = useCallback(
        (inputWorkflowFile: IInputWorkflowFile) => () => {
            if (inputWorkflowFile.id) {
                importInvoiceFilesStore.deleteInputWorkflowFile(inputWorkflowFile.id);
            }
        },
        [importInvoiceFilesStore]
    );

    const onClickOkInputWorkflowFile = useCallback(
        (inputWorkflowFile: IInputWorkflowFile) => async () => {
            if (inputWorkflowFile.id) {
                await importInvoiceFilesStore.updateInputWorkWorkflowFileState(
                    inputWorkflowFile.id,
                    INPUTWORKFLOW_FILE_STATE.READY
                );
            }
        },
        [importInvoiceFilesStore]
    );

    const getInputWorkflowFileActionElements = (inputWorkflowFile: IInputWorkflowFile) => {
        const actionElements = [];

        const deleteIcon = (
            <UI.Icon
                key={"deleteIcon" + inputWorkflowFile.id}
                icon={UI.SVGIcon.Trash}
                size={30}
                className={`ml-2 ${styles.DeleteIcon}`}
                onClick={onDeleteInputWorkflowFile(inputWorkflowFile)}
            />
        );

        const splitIcon = (
            <a
                key={"splitKrediFlowIconContainer" + inputWorkflowFile.id}
                href={selectRoute(Route.creditorsImportSplitPDF, authStore.user?.role, {
                    fileid: inputWorkflowFile.fileId ?? ""
                })}
                className={`ml-2 ${styles.SplitKrediFlowIconContainer} ${
                    inputWorkflowFile.state === INPUTWORKFLOW_FILE_STATE.MANUAL ? styles.Full : styles.Outline
                }`}
            >
                <UI.Icon
                    key={"splitIcon" + inputWorkflowFile.id}
                    className={`ml-2 ${styles.SplitKrediFlowIcon}`}
                    icon={UI.SVGIcon.Split}
                    size={30}
                />
            </a>
        );

        const okInputWorkflowFileButton = (
            <UI.Button
                key={"okInputWorkflowFileButton" + inputWorkflowFile.id}
                label={
                    importInvoiceFilesStore.isWorkflowFileStateUpdating.has(inputWorkflowFile.id ?? "") ? (
                        <UI.RotatingSpinner noLogo size={20} />
                    ) : (
                        t("labels.ok").toLocaleUpperCase()
                    )
                }
                className={`${styles.OkKrediFlowButton} default-button-height`}
                variant="outline-primary"
                onClick={onClickOkInputWorkflowFile(inputWorkflowFile)}
            />
        );

        if (inputWorkflowFile.state) {
            switch (inputWorkflowFile.state) {
                case INPUTWORKFLOW_FILE_STATE.NEW:
                    actionElements.push(deleteIcon);
                    break;
                case INPUTWORKFLOW_FILE_STATE.MANUAL:
                    actionElements.push(okInputWorkflowFileButton);
                    actionElements.push(splitIcon);
                    actionElements.push(deleteIcon);
                    break;
                case INPUTWORKFLOW_FILE_STATE.READY:
                    actionElements.push(splitIcon);
                    actionElements.push(deleteIcon);
                    break;
            }
        }

        return actionElements;
    };

    // Renders all the files of a import workflow (which is part of an item in the list)
    const renderImportInvoiceListItemBodyRow = (inputWorkflowFile: IInputWorkflowFile) => {
        const colIconAndDescription = (
            <div style={{ flex: columnWidths[COLUMN.DESCRIPTION] }} className="d-flex">
                <a
                    href={NetworkConfig.datafileUrl + inputWorkflowFile.fileId}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-flex"
                >
                    <span
                        style={{
                            paddingLeft: inputWorkflowFile.level ? (inputWorkflowFile.level + 1) * 10 : 10
                        }}
                    >
                        <UI.Icon icon={UI.SVGIcon.Documents} color={ColorStyle("primary")} size={25} className="mr-1" />
                    </span>
                    <span>{inputWorkflowFile.name}</span>
                </a>
            </div>
        );

        const colState = (
            <div style={{ flex: columnWidths[COLUMN.STATE] }}>
                {getInputWorkflowFileStateElement(inputWorkflowFile.state)}
            </div>
        );

        const colActions = (
            <div style={{ flex: columnWidths[COLUMN.ACTIONS] }} className="text-right">
                <div className="mr-1 d-flex align-items-center justify-content-end">
                    {getInputWorkflowFileActionElements(inputWorkflowFile)}
                </div>
            </div>
        );

        const colToggle = <div style={{ flex: columnWidths[COLUMN.TOGGLE] }}></div>;

        return (
            <div className={styles.ImportInvoiceListItemBodyRow} key={inputWorkflowFile.id}>
                {colIconAndDescription}
                {colState}
                {colActions}
                {colToggle}
            </div>
        );
    };

    const getInputWorkflowStateElement = (inputWorkflowState: INPUTWORKFLOW_STATE | undefined) => {
        const stateText = t(`screens.kredi_flow.document_import.input_workflow.state.${inputWorkflowState}`);

        let stateElement = undefined;

        if (inputWorkflowState) {
            switch (inputWorkflowState) {
                case INPUTWORKFLOW_STATE.UI:
                    stateElement = (
                        <div className="d-flex align-items-center">
                            <div>
                                <UI.RotatingSpinner noLogo size={20} />
                            </div>
                            <div>{stateText}</div>
                        </div>
                    );
                    break;
                case INPUTWORKFLOW_STATE.NEW:
                case INPUTWORKFLOW_STATE.MANUAL:
                    stateElement = stateText;
                    break;
                case INPUTWORKFLOW_STATE.ANALYZING:
                case INPUTWORKFLOW_STATE.CREATING_INVOICES:
                    stateElement = (
                        <div className="d-flex align-items-center">
                            <div>
                                <UI.RotatingSpinner noLogo size={20} />
                            </div>
                            <div>{stateText}</div>
                        </div>
                    );
                    break;
                case INPUTWORKFLOW_STATE.FINISHED:
                    stateElement = <div className={`${styles.InputWorkflowTag} ${styles.Finished}`}>{stateText}</div>;
                    break;
            }
        }

        return stateElement;
    };

    const onDeleteInputWorkflow = useCallback(
        (inputWorkflow: IInputWorkflow) => () => {
            importInvoiceFilesStore.deleteInputWorkflow(inputWorkflow);
        },
        [importInvoiceFilesStore]
    );

    const onStartKrediFlow = useCallback(
        (inputWorkflow: IInputWorkflow) => async (e: any) => {
            importInvoiceFilesStore.toggleInputWorkflowFileDropdowns(inputWorkflow.id);
            await importInvoiceFilesStore.startKrediFlow(inputWorkflow);
        },
        [importInvoiceFilesStore]
    );

    const displayDeleteInputWorkflowModal = useCallback(
        (inputWorkflow: IInputWorkflow) => () => {
            importInvoiceFilesStore.setInputWorkflowToDelete(inputWorkflow);
            importInvoiceFilesStore.setIsDeleteInputWorkflowModalDisplayed(true);
        },
        [importInvoiceFilesStore]
    );

    const hideDeleteInputWorkflowModal = useCallback(() => {
        importInvoiceFilesStore.setInputWorkflowToDelete(undefined);
        importInvoiceFilesStore.setIsDeleteInputWorkflowModalDisplayed(false);
    }, [importInvoiceFilesStore]);

    const getInputWorkflowActionElements = (inputWorkflow: IInputWorkflow) => {
        const actionElements = [];

        const deleteIcon = (
            <UI.Icon
                key={"deleteIcon" + inputWorkflow.id}
                icon={UI.SVGIcon.Trash}
                size={30}
                className={`ml-2 ${styles.DeleteIcon}`}
                onClick={displayDeleteInputWorkflowModal(inputWorkflow)}
            />
        );

        const startKrediFlowButton = (
            <UI.Button
                key={"startKrediFlowButton" + inputWorkflow.id}
                label={t("screens.kredi_flow.document_import.list.start_kredi_flow")}
                className={`${styles.StartKrediFlowButton} default-button-height`}
                disabled={
                    !importInvoiceFilesStore.workflowsReadyToCreateInvoices.some(
                        (workflow) => workflow.id === inputWorkflow.id
                    ) || importInvoiceFilesStore.loadingState === LOADING_STATE.STARTING_KREDI_FLOW
                }
                onClick={onStartKrediFlow(inputWorkflow)}
            />
        );

        if (inputWorkflow.state) {
            switch (inputWorkflow.state) {
                case INPUTWORKFLOW_STATE.NEW:
                case INPUTWORKFLOW_STATE.ANALYZING:
                    actionElements.push(deleteIcon);
                    break;
                case INPUTWORKFLOW_STATE.MANUAL:
                    actionElements.push(startKrediFlowButton);
                    actionElements.push(deleteIcon);
                    break;
            }
        }

        return actionElements;
    };

    // Renders a row in the list which stands for one 'input workflow'
    const renderImportInvoiceListItem = (inputWorkflow: IInputWorkflow) => {
        const isInputWorkflowFileDropdownDisplayed = importInvoiceFilesStore.displayedInputWorkflowDropdowns.has(
            inputWorkflow.id
        );

        const inputWorkflowDescription = importInvoiceFilesStore.getInputWorkflowName(inputWorkflow);

        const colIconAndDescription = (
            <div style={{ flex: columnWidths[COLUMN.DESCRIPTION] }} className="d-flex align-items-center mb-2">
                <UI.Icon
                    icon={inputWorkflow.origin === INVOICE_ORIGIN.EMAIL ? UI.SVGIcon.Mail : UI.SVGIcon.Documents}
                    color={ColorStyle("primary")}
                    size={inputWorkflow.origin === INVOICE_ORIGIN.EMAIL ? 30 : 25}
                    className="mr-1"
                />
                <span className="bold-text">{inputWorkflowDescription}</span>
            </div>
        );

        const colState = (
            <div style={{ flex: columnWidths[COLUMN.STATE] }}>{getInputWorkflowStateElement(inputWorkflow.state)}</div>
        );

        const colActions = (
            <div style={{ flex: columnWidths[COLUMN.ACTIONS] }} className="text-right">
                <div className="mr-1 d-flex align-items-center justify-content-end">
                    {getInputWorkflowActionElements(inputWorkflow)}
                </div>
            </div>
        );

        const colToggle = (
            <div style={{ flex: columnWidths[COLUMN.TOGGLE] }} className="d-flex">
                <UI.Icon
                    icon={isInputWorkflowFileDropdownDisplayed ? UI.SVGIcon.ChevronUpThin : UI.SVGIcon.ChevronDownThin}
                    color={ColorStyle("primary")}
                />
            </div>
        );

        const listItemHeader = (
            <div
                className={styles.ImportInvoiceListItemHeader}
                onClick={toggleDisplayedInputWorkflowFileDropdown(inputWorkflow.id)}
            >
                {colIconAndDescription}
                {colState}
                {colActions}
                {colToggle}
            </div>
        );

        const listItemBody = (
            <div
                className={`${styles.ImportInvoiceListItemBody} ${
                    isInputWorkflowFileDropdownDisplayed ? styles.open : styles.closed
                }`}
            >
                {inputWorkflow.inputWorkflowFiles &&
                    inputWorkflow.inputWorkflowFiles.map((inputWorkflowFile) =>
                        renderImportInvoiceListItemBodyRow(inputWorkflowFile)
                    )}
            </div>
        );

        return (
            <div key={inputWorkflow.id} className={styles.ImportInvoiceListItem}>
                {listItemHeader}
                {listItemBody}
            </div>
        );
    };

    const importInvoiceListHeader = (
        <div className={`${styles.ImportInvoiceListHeader}`}>
            <div style={{ flex: columnWidths[COLUMN.DESCRIPTION] }} className="d-flex align-items-center mb-2">
                Bezeichnung
            </div>
            <div style={{ flex: columnWidths[COLUMN.STATE] }}>{t("screens.kredi_flow.document_import.list.state")}</div>
            <div style={{ flex: columnWidths[COLUMN.ACTIONS] }} className="text-right">
                <div className="mr-1">{t("screens.kredi_flow.document_import.list.action")}</div>
            </div>
            <div style={{ flex: columnWidths[COLUMN.TOGGLE] }} className="d-flex"></div>
        </div>
    );

    const deleteInputWorkflowModal = (
        <UI.Modal size="lg" show={importInvoiceFilesStore.isDeleteInputWorkflowModalDisplayed} centered={true}>
            <div className={styles.ModalTitle}>
                {t("screens.kredi_flow.document_import.delete_modal.delete_import")}
            </div>
            <div>{t("screens.kredi_flow.document_import.delete_modal.confirmation_message")}</div>
            <div className={styles.ButtonContainer}>
                <UI.Button
                    label={t("labels.cancel")}
                    className={styles.ModalButtonCancel}
                    onClick={hideDeleteInputWorkflowModal}
                />
                <UI.Button
                    label={
                        (importInvoiceFilesStore.loadingState === LOADING_STATE.DELETING_INPUT_WORKFLOW) === true ? (
                            <UI.RotatingSpinner className={styles.LoadSpinner} size={20} noLogo />
                        ) : (
                            t("labels.ok").toLocaleUpperCase()
                        )
                    }
                    className={styles.ModalButtonDelete}
                    onClick={
                        importInvoiceFilesStore.inputWorkflowToDelete
                            ? onDeleteInputWorkflow(importInvoiceFilesStore.inputWorkflowToDelete)
                            : undefined
                    }
                    disabled={importInvoiceFilesStore.loadingState === LOADING_STATE.DELETING_INPUT_WORKFLOW}
                />
            </div>
        </UI.Modal>
    );

    const importInvoicesList = (
        <>
            {deleteInputWorkflowModal}
            <div className={styles.ImportInvoiceListContainer}>
                {importInvoiceListHeader}
                {importInvoiceFilesStore.filteredInputWorkflows.map((inputWorkflow) =>
                    renderImportInvoiceListItem(inputWorkflow)
                )}
            </div>
        </>
    );

    return importInvoiceFilesStore.loadingState === LOADING_STATE.LOADING ? loadingImportElement : importInvoicesList;
};

export const ImportInvoicesList = Sentry.withProfiler(observer(ImportInvoicesListBase));
